import {useCallback, useEffect, useMemo, useState} from "react";
import {ReachSegment, ReachSegmentCreatePayload, SegmentsBatchPayload} from "../../apis/requests/segments/types";
import {keepCategoriesTogether} from "./segmentCategoryBlocks";

export type StagedStatus = "new" | "edited" | "deleted";

export type StagedSegment = ReachSegment & {stagedStatus?: StagedStatus};

type StagedChanges = {
  created: ReachSegment[];
  editedById: Record<number, ReachSegmentCreatePayload>;
  deletedIds: number[];
  orderedIds: number[] | null;
};

const EMPTY_CHANGES: StagedChanges = {created: [], editedById: {}, deletedIds: [], orderedIds: null};

const isExclusive = (segment: ReachSegment) => segment.type === "EXCLUSIVE";

const exclusiveFirst = (segments: StagedSegment[]) =>
  keepCategoriesTogether([...segments].sort((a, b) => Number(isExclusive(b)) - Number(isExclusive(a))));

const toPayload = ({
  name,
  description,
  priority,
  type,
  segment_category,
  is_category_fallback,
  conditions,
}: ReachSegment): ReachSegmentCreatePayload => ({
  name,
  description,
  priority,
  type,
  segment_category,
  is_category_fallback,
  conditions,
});

const markOrphanedCategoryFallbacksDeleted = (segments: StagedSegment[]): StagedSegment[] => {
  const categoriesWithBuckets = new Set(
    segments
      .filter((segment) => !segment.is_category_fallback && segment.stagedStatus !== "deleted")
      .map((segment) => segment.segment_category)
  );
  return segments.map((segment) =>
    segment.is_category_fallback && !categoriesWithBuckets.has(segment.segment_category)
      ? {...segment, stagedStatus: "deleted"}
      : segment
  );
};

export const useStagedSegments = (serverSegments: ReachSegment[]) => {
  const [changes, setChanges] = useState<StagedChanges>(EMPTY_CHANGES);

  const segments = useMemo<StagedSegment[]>(() => {
    const staged: StagedSegment[] = [
      ...serverSegments.map((segment): StagedSegment => {
        if (changes.deletedIds.includes(segment.id)) return {...segment, stagedStatus: "deleted"};
        const edit = changes.editedById[segment.id];
        return edit ? {...segment, ...edit, stagedStatus: "edited"} : segment;
      }),
      ...changes.created.map((segment): StagedSegment => ({...segment, stagedStatus: "new"})),
    ];
    const withOrphansDeleted = markOrphanedCategoryFallbacksDeleted(staged);
    if (!changes.orderedIds) return exclusiveFirst(withOrphansDeleted);
    const positionById = new Map(changes.orderedIds.map((id, position) => [id, position]));
    const ordered = [...withOrphansDeleted].sort(
      (a, b) => (positionById.get(a.id) ?? Infinity) - (positionById.get(b.id) ?? Infinity)
    );
    return exclusiveFirst(ordered);
  }, [serverSegments, changes]);

  const changeCount =
    changes.created.length +
    Object.keys(changes.editedById).length +
    changes.deletedIds.length +
    (changes.orderedIds ? 1 : 0);

  const stageCreate = useCallback((payload: ReachSegmentCreatePayload) => {
    setChanges((current) => {
      const tempId = Math.min(0, ...current.created.map((segment) => segment.id)) - 1;
      return {...current, created: [...current.created, {...payload, id: tempId, principal: null}]};
    });
  }, []);

  const stageUpdate = useCallback((id: number, payload: ReachSegmentCreatePayload) => {
    setChanges((current) =>
      id < 0
        ? {
            ...current,
            created: current.created.map((segment) => (segment.id === id ? {...segment, ...payload} : segment)),
          }
        : {...current, editedById: {...current.editedById, [id]: payload}}
    );
  }, []);

  const stageDelete = useCallback((id: number) => {
    setChanges((current) =>
      id < 0
        ? {...current, created: current.created.filter((segment) => segment.id !== id)}
        : {...current, deletedIds: [...current.deletedIds, id]}
    );
  }, []);

  const undo = useCallback((id: number) => {
    setChanges((current) => {
      const {[id]: _undone, ...editedById} = current.editedById;
      return {
        ...current,
        created: current.created.filter((segment) => segment.id !== id),
        editedById,
        deletedIds: current.deletedIds.filter((deletedId) => deletedId !== id),
      };
    });
  }, []);

  const stageOrder = useCallback((orderedIds: number[]) => {
    setChanges((current) => ({...current, orderedIds}));
  }, []);

  const discardAll = useCallback(() => setChanges(EMPTY_CHANGES), []);

  const buildBatchPayload = useCallback(
    (): SegmentsBatchPayload => ({
      creates: changes.created.map((segment) => ({...toPayload(segment), temp_id: segment.id})),
      updates: Object.entries(changes.editedById)
        .filter(([id]) => !changes.deletedIds.includes(Number(id)))
        .map(([id, segment]) => ({id: Number(id), segment})),
      delete_ids: changes.deletedIds,
      ordered_ids: segments.filter((segment) => segment.stagedStatus !== "deleted").map((segment) => segment.id),
    }),
    [changes, segments]
  );

  useEffect(() => {
    if (!changeCount) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [changeCount]);

  return {
    segments,
    changeCount,
    stageCreate,
    stageUpdate,
    stageDelete,
    stageOrder,
    undo,
    discardAll,
    buildBatchPayload,
  };
};
